import type {
  SharedUrl,
  SheetCustomModelFieldsFragment,
  SheetThreadCommentNotification,
  ViewFieldsFragment,
} from '@salescore/client-api'
import type { CachedView, ChangeHistory } from '@salescore/client-redux'
import {
  CORE_CONSTANT,
  type ViewConfigAdditionalConfig,
  viewConfigFilterSchema,
  type ViewQueryRecordNode,
} from '@salescore/core'
import { isSharedLinkPresetName } from '@salescore/core'
import { useEffect } from 'react'
import { type MutableSnapshot, useSetRecoilState } from 'recoil'
import { z } from 'zod'

import type { ViewsContext } from '../../components/ViewComponent'
import type { SharedUrlKind } from '../../domain/applySharedUrlToView'
import { kpiParameterAtom, kpiPivotPickedPresetNameAtom, sheetPickedPresetNameAtom } from '../navigation/atoms'
import { convertForRecordsImpl, recordIdsAtom, recordsImplAtom } from '../records'
import { additionalConfigAtom, mustacheParameterAtom } from '../records/atoms'
import { changesAtom } from '../recordsHistory'
import {
  autoDrillDownParameterAtom,
  kpiFormAtom,
  kpiFormModifiedAtom,
  sheetCustomModelsAtom,
  viewsContextAtom,
} from '../view/atoms'
import { attachMetricToConfigFromFormula } from '../view/attachMetricToKpiConfig'
import { generateInitialKpiForm } from '../view/mutations/initializeViewMutation'
import { configAtom } from '../viewConfig'

type InitializeState = ((mutableSnapshot: MutableSnapshot) => void) | undefined

export interface InitializeStateProperties {
  mustacheParameter: Record<string, unknown>
  additionalConfig: ViewConfigAdditionalConfig | undefined
  viewsContext: ViewsContext
}

function extractAdditionalConfigFilterLeafs(sharedUrl?: SharedUrl) {
  const parsedSheetThreadCommentInKpiPivotDrillDown = z
    .object({
      additionalConfigFilterLeafs: z
        .object({
          type: z.enum([`drillDown`]),
          leaf: viewConfigFilterSchema,
        })
        .array(),
    })
    .safeParse(sharedUrl?.parameter)
  if (parsedSheetThreadCommentInKpiPivotDrillDown.success) {
    return parsedSheetThreadCommentInKpiPivotDrillDown.data.additionalConfigFilterLeafs
  }
}

const restoreLatestRecords = (changes?: ChangeHistory[]): ViewQueryRecordNode[] => {
  const latestChange = changes?.at(-1)
  if (latestChange === undefined) {
    return []
  }
  try {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    return JSON.parse(latestChange.snapshotAfterChange) as ViewQueryRecordNode[]
  } catch {
    return []
  }
}

/**
 * ViewComponent 内部の `RecoilRoot` の `initializeState` プロパティに渡す関数。
 *
 * 受け取った props (view / client / me / additionalConfig / etc.) を Recoil の各 atom に
 * `set` し、内部 Recoil 世界の初期状態を組み立てます。関数を返す 2 段構造 (引数 → `set`)
 * になっているのは Recoil の API 仕様に合わせるためです。
 */
export const initializeState =
  ({
    mustacheParameter,
    additionalConfig,
    viewsContext,
    sharedUrlApplied,
    cachedView,
    sheetCustomModels,
    sharedUrl,
  }: InitializeStateProperties & {
    sharedUrlApplied: {
      kind?: SharedUrlKind
      view: ViewFieldsFragment
    }
    cachedView: CachedView | undefined
    sheetCustomModels: SheetCustomModelFieldsFragment[]
    sharedUrl: SharedUrl | undefined
    sheetThreadCommentNotifications: SheetThreadCommentNotification[]
  }): InitializeState =>
  // eslint-disable-next-line complexity
  (snapshot) => {
    const { set } = snapshot

    set(mustacheParameterAtom, mustacheParameter)
    set(viewsContextAtom, viewsContext)
    const { view } = sharedUrlApplied
    const shouldApplyCachedConfig = sharedUrlApplied.kind === undefined

    // viewの変更時、まとめてconfigなどを変更する
    // viewの変更とconfigの変更を別々のhooksで行なったりすると、新しいviewId・古いconfigの状態でmutationが走ったりするので注意
    // キャッシュがある場合はキャッシュから復元する
    set(changesAtom, cachedView?.changes ?? [])
    const { recordIds, recordsImpl } = convertForRecordsImpl(restoreLatestRecords(cachedView?.changes))
    set(recordIdsAtom, recordIds)
    set(recordsImplAtom, recordsImpl)

    // configAtom は ViewConfigWithMetric を要求する。server / cache どちらの経路でも
    // source of truth は formula.metric とし、seed 境界で 1 回だけ持ち上げる。
    const initialConfig = shouldApplyCachedConfig && cachedView?.config !== undefined ? cachedView.config : view.config
    set(configAtom, attachMetricToConfigFromFormula(initialConfig))
    set(additionalConfigAtom, additionalConfig ?? cachedView?.additionalConfig ?? {})

    // kpiのフォーム用の対応
    if (view.config.type === 'kpi') {
      set(kpiFormAtom, cachedView?.kpiForm ?? generateInitialKpiForm({ viewName: view.name, config: view.config }))
      set(kpiFormModifiedAtom, cachedView?.isModifiedKpiForm ?? false)
    }

    set(sheetCustomModelsAtom, sheetCustomModels)

    if (
      (sharedUrlApplied.kind === 'sheetThreadComment' || sharedUrlApplied.kind === 'sheetSharedUrl') &&
      sharedUrlApplied.view.config.type === 'sheet'
    ) {
      const sharedLinkPresetName = sharedUrlApplied.view.config.presets?.find((preset) =>
        isSharedLinkPresetName(preset.name),
      )?.name
      set(sheetPickedPresetNameAtom, sharedLinkPresetName)
    } else {
      set(sheetPickedPresetNameAtom, cachedView?.sheetPickedPresetName)
    }

    if (
      (sharedUrlApplied.kind === 'kpiPivotSharedUrl' ||
        sharedUrlApplied.kind === 'sheetThreadCommentInKpiPivotDrillDown') &&
      sharedUrlApplied.view.config.type === 'kpiPivot'
    ) {
      const sharedLinkPresetName = sharedUrlApplied.view.config.presets?.find((preset) =>
        isSharedLinkPresetName(preset.name),
      )?.name
      set(kpiPivotPickedPresetNameAtom, sharedLinkPresetName)
      set(kpiParameterAtom, (oldValue) => {
        if (sharedUrlApplied.view.config.type !== 'kpiPivot') {
          return oldValue
        }
        const sharedLinkPreset = sharedUrlApplied.view.config.presets?.find((preset) =>
          isSharedLinkPresetName(preset.name),
        )
        return {
          ...oldValue,
          pivot: sharedLinkPreset?.parameter.pivot ?? oldValue.pivot,
          queryParameter: {
            ...oldValue.queryParameter,
            ...sharedLinkPreset?.parameter.queryParameter,
          },
          goalConfig: sharedLinkPreset?.parameter.goalConfig,
        }
      })
    } else {
      set(kpiPivotPickedPresetNameAtom, cachedView?.kpiPivotPickedPresetName)
    }

    if (sharedUrlApplied.kind === 'sheetThreadCommentInKpiPivotDrillDown') {
      const additionalFilterLeafsWithPrevious = extractAdditionalConfigFilterLeafs(sharedUrl)
      const sheetViewId = z
        .object({
          sheetViewId: z.string().refine((x) => x.startsWith(CORE_CONSTANT.KPI_SHEET_DYNAMIC_VIEW_ID_PREFIX)),
        })
        .safeParse(sharedUrl?.parameter)
      if (additionalFilterLeafsWithPrevious !== undefined && sheetViewId.success) {
        const kpiViewId = sheetViewId.data.sheetViewId.replace(CORE_CONSTANT.KPI_SHEET_DYNAMIC_VIEW_ID_PREFIX, '')
        set(autoDrillDownParameterAtom, {
          type: 'kpi',
          dimensionValues: {
            rows: [],
            columns: [],
          },
          additionalFilterLeafsWithPrev: additionalFilterLeafsWithPrevious,
          viewId: kpiViewId,
          queryParameter: {
            periodName: '全期間',
          },
        })
      }
    }

    if (viewsContext.shouldInitializeWithNewRecord === true) {
      const recordNodes = [
        {
          id: CORE_CONSTANT.VIEW_NEW_RECORD_PREFIX, // prefixのみで十分なはず
          attributes: {},
          children: [],
          meta: {
            height: 1,
            innerRowIndexStart: 0,
            innerRowIndexEnd: 1,
          },
        },
      ]
      const { recordIds, recordsImpl } = convertForRecordsImpl(recordNodes)
      set(recordIdsAtom, recordIds)
      set(recordsImplAtom, recordsImpl)
    }
  }

export const useReInitializeState = ({ viewsContext }: InitializeStateProperties) => {
  const setViewsContext = useSetRecoilState(viewsContextAtom)
  // 一部の値は書き変わることがあるので、書きかわった場合にatomsも初期化する
  useEffect(() => {
    setViewsContext(viewsContext)
  }, [viewsContext])
}
